'use client';

import { useState, useEffect } from 'react';
import { TranslationProvider } from '@/lib/i18n/TranslationProvider';
import Hero from '@/components/sections/Hero';
import TrustStrip from '@/components/sections/TrustStrip';
import Gallery from '@/components/sections/Gallery';
import Security from '@/components/sections/Security';
import Pricing from '@/components/sections/Pricing';
import FAQ from '@/components/sections/FAQ';
import FeaturedOn from '@/components/sections/FeaturedOn';
import HowToExport from '@/components/sections/HowToExport';
import ExportFeatures from '@/components/sections/ExportFeatures';
import GuidePreview from '@/components/sections/GuidePreview';
import PrivacyBriefing from '@/components/sections/PrivacyBriefing';
import TrustSignals from '@/components/sections/TrustSignals';
import VideoShowcase from '@/components/sections/VideoShowcase';
import { useVideoExperience } from '@/components/video/VideoExperienceProvider';

export default function Home() {
  const { setPiPVisibility, openModal } = useVideoExperience();
  const [isVideoShowcaseInView, setIsVideoShowcaseInView] = useState(false);
  const [hasScrolledPastShowcase, setHasScrolledPastShowcase] = useState(false);

  // Track if user has scrolled past the showcase
  useEffect(() => {
    const handleScroll = () => {
      const showcaseElement = document.getElementById('demo');
      if (showcaseElement) {
        const rect = showcaseElement.getBoundingClientRect();
        // If the bottom of the showcase is above the viewport, we've scrolled past it
        if (rect.bottom < 0) {
          setHasScrolledPastShowcase(true);
        } else if (rect.top > 0) {
          // If we scroll back up above it, hide PiP
          setHasScrolledPastShowcase(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update global PiP visibility based on home page scroll/intersection
  useEffect(() => {
    setPiPVisibility(!isVideoShowcaseInView && hasScrolledPastShowcase);
  }, [isVideoShowcaseInView, hasScrolledPastShowcase, setPiPVisibility]);

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "WaVault",
    "url": "https://www.houseof2.studio"
  };

  return (
    <TranslationProvider locale="en">
      <div className="min-h-screen bg-[var(--color-md-surface)] noise-bg text-[var(--color-md-on-surface)] selection:bg-[var(--color-md-primary)]/30">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
        />
        <Hero />
        <TrustStrip />
        <VideoShowcase 
          onVisibilityChange={setIsVideoShowcaseInView} 
          onExpand={openModal}
        />
        <PrivacyBriefing />
        <HowToExport />
        <ExportFeatures />
        <GuidePreview />
        <div id="features">
          <Gallery />
        </div>
        <FeaturedOn />
        <Security />
        <Pricing />
        <TrustSignals />
        <FAQ />
      </div>
    </TranslationProvider>
  );
}
